import { EXECUTIVE_OFFICES } from '../constants/offices'
import { ExecutiveLocationType, TrackerStartLocationPayload } from '../types'

const GEOLOCATION_MAX_ATTEMPTS = 3
const GEOLOCATION_TIMEOUT_MS = 12000
const REVERSE_GEOCODE_TIMEOUT_MS = 4000

type CaptureFailureReason = 'permission' | 'unsupported' | 'unavailable'

class ExecutiveLocationCaptureError extends Error {
  reason: CaptureFailureReason

  attempts: number

  constructor(message: string, reason: CaptureFailureReason, attempts: number) {
    super(message)
    this.name = 'ExecutiveLocationCaptureError'
    this.reason = reason
    this.attempts = attempts
  }
}

const buildFallbackAddress = (latitude: number, longitude: number) =>
  `Cerca de ${latitude.toFixed(5)}, ${longitude.toFixed(5)}`

const getCurrentPosition = () =>
  new Promise<GeolocationPosition>((resolve, reject) => {
    if (!window.navigator.geolocation) {
      reject(new ExecutiveLocationCaptureError('Este navegador no soporta geolocalizacion.', 'unsupported', 0))
      return
    }

    window.navigator.geolocation.getCurrentPosition(resolve, reject, {
      enableHighAccuracy: true,
      timeout: GEOLOCATION_TIMEOUT_MS,
      maximumAge: 0
    })
  })

const resolveApproximateAddress = async (latitude: number, longitude: number) => {
  const fallbackAddress = buildFallbackAddress(latitude, longitude)
  const controller = new AbortController()
  const timeoutId = window.setTimeout(() => controller.abort(), REVERSE_GEOCODE_TIMEOUT_MS)

  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${encodeURIComponent(
        String(latitude)
      )}&lon=${encodeURIComponent(String(longitude))}`,
      {
        method: 'GET',
        signal: controller.signal,
        headers: {
          Accept: 'application/json'
        }
      }
    )

    if (!response.ok) {
      return fallbackAddress
    }

    const data = (await response.json()) as { display_name?: string; name?: string }
    return data.display_name || data.name || fallbackAddress
  } catch (error) {
    return fallbackAddress
  } finally {
    window.clearTimeout(timeoutId)
  }
}

const toCaptureError = (error: unknown, attempts: number) => {
  if (error instanceof ExecutiveLocationCaptureError) {
    return error
  }

  if (typeof error === 'object' && error !== null && 'code' in error) {
    const geolocationError = error as GeolocationPositionError

    if (geolocationError.code === geolocationError.PERMISSION_DENIED) {
      return new ExecutiveLocationCaptureError(
        'No pudimos obtener tu ubicacion. Revisa que el navegador tenga permisos de ubicacion habilitados para continuar.',
        'permission',
        attempts
      )
    }

    if (geolocationError.code === geolocationError.POSITION_UNAVAILABLE) {
      return new ExecutiveLocationCaptureError(
        'No pudimos determinar tu ubicacion actual. Verifica tu senal GPS e intenta de nuevo.',
        'unavailable',
        attempts
      )
    }
  }

  return new ExecutiveLocationCaptureError(
    'No pudimos obtener tu ubicacion actual. Intenta nuevamente y revisa los permisos del navegador.',
    'unavailable',
    attempts
  )
}

const captureExecutiveStartLocation = async (
  locationType: ExecutiveLocationType
): Promise<TrackerStartLocationPayload> => {
  let lastError: ExecutiveLocationCaptureError | null = null

  for (let attempt = 1; attempt <= GEOLOCATION_MAX_ATTEMPTS; attempt += 1) {
    try {
      const position = await getCurrentPosition()
      const latitude = Number(position.coords.latitude)
      const longitude = Number(position.coords.longitude)
      const accuracyMeters = Number.isFinite(position.coords.accuracy) ? Number(position.coords.accuracy) : null
      const address = await resolveApproximateAddress(latitude, longitude)

      return {
        locationType,
        latitude,
        longitude,
        address,
        capturedAt: new Date().toISOString(),
        accuracyMeters,
        permissionAttempts: attempt
      }
    } catch (error) {
      lastError = toCaptureError(error, attempt)
    }
  }

  if (lastError) {
    throw lastError
  }

  throw new ExecutiveLocationCaptureError(
    'No pudimos obtener tu ubicacion. Revisa los permisos e intenta nuevamente.',
    'unavailable',
    GEOLOCATION_MAX_ATTEMPTS
  )
}

const isExecutiveLocationCaptureError = (error: unknown): error is ExecutiveLocationCaptureError =>
  error instanceof ExecutiveLocationCaptureError

const getExecutiveLocationHelpCopy = () =>
  `Para registrar como ejecutivo debes permitir el acceso a tu ubicacion en el navegador. Actualmente la oficina habilitada es ${EXECUTIVE_OFFICES[0].name}.`

export {
  GEOLOCATION_MAX_ATTEMPTS,
  captureExecutiveStartLocation,
  getExecutiveLocationHelpCopy,
  isExecutiveLocationCaptureError
}
